import {RcfaComplianceLocationHistory, RcfaComplianceLocationNode} from '@domain/rcfa'
import {HierarchyNodeType} from '@domain/shared'
import {useState} from 'react'

import {findPathToNode, resolveNavigationFromHistory} from '../utils'

export const useRcfaComplianceCardsNavigation = (
  rootLocation: RcfaComplianceLocationNode[],
  onPlantItemClick: (plantId: string) => void
) => {
  const [locationHistory, setLocationHistory] = useState<RcfaComplianceLocationHistory[]>([])
  const [locationSiblings, setLocationSiblings] = useState<RcfaComplianceLocationHistory[]>([])

  const getCurrentLocations = () => {
    return locationHistory.reduce<RcfaComplianceLocationNode | RcfaComplianceLocationNode[]>(
      (current, id) => {
        if (!current) return rootLocation
        if (Array.isArray(current)) {
          return current.find((node) => node.id === id.id)?.subLocations || []
        }
        return current.subLocations?.find((node) => node.id === id.id)?.subLocations || []
      },
      rootLocation
    )
  }

  const enterLocation = (location: RcfaComplianceLocationNode) => {
    if (location.type === HierarchyNodeType.Plant) {
      onPlantItemClick(location.id)
      return
    }

    const current = getCurrentLocations()
    if (Array.isArray(current)) {
      setLocationSiblings(current)
    }

    setLocationHistory((prev) => [...prev, {id: location.id, name: location.name}])
  }

  const goToLocation = (id: string) => {
    if (id === '') {
      setLocationHistory([])
      setLocationSiblings([])
      return
    }

    const result = resolveNavigationFromHistory(rootLocation, locationHistory, id)

    if (result) {
      setLocationHistory(result.newHistory)
      setLocationSiblings(result.siblings)
      return
    }

    const path = findPathToNode(rootLocation, id)
    if (path) {
      setLocationHistory(path)

      const parentPath = path.slice(0, -1)
      const current = parentPath.reduce<RcfaComplianceLocationNode | RcfaComplianceLocationNode[]>(
        (current, historyItem) => {
          if (!current) return rootLocation
          if (Array.isArray(current)) {
            return current.find((node) => node.id === historyItem.id)?.subLocations || []
          }
          return (
            current.subLocations?.find((node) => node.id === historyItem.id)?.subLocations || []
          )
        },
        rootLocation
      )

      if (Array.isArray(current)) {
        setLocationSiblings(current)
      } else {
        setLocationSiblings([])
      }
    } else {
      setLocationHistory([])
      setLocationSiblings([])
    }
  }

  return {
    currentLocations: getCurrentLocations(),
    locationHistory,
    enterLocation,
    goToLocation,
    locationSiblings
  }
}
